import React from 'react';
import { Link } from 'react-router-dom';

import LegalPage, { Section, List, Table } from '../_gateway/LegalPage';
import {
  CONTACT_EMAIL, COOKIE_HEADERS, NECESSARY_COOKIES, ANALYTICS_COOKIES, MARKETING_COOKIES,
} from '../_gateway/legal-content';
import { openCookieSettings } from '_components/atoms/cookie-consent';

const CookieSettingsLink = ({ children }) => (
  <button type="button" onClick={openCookieSettings} style={{ padding: 0, border: 0, background: 'none', color: '#1670c8', font: 'inherit', cursor: 'pointer' }}>
    {children}
  </button>
);

const CookiePolicy = () => (
  <LegalPage title="Cookie Policy" lastUpdated="7 October 2026">

    <Section id="about" number={1} title="About this policy">
      <p>
        This policy explains how PRM Assist Limited (&quot;PRM Assist&quot;, &quot;we&quot;, &quot;us&quot;) uses cookies and similar technologies
        on www.prmassist.com (the &quot;Website&quot;). It should be read with our <Link to="/website-privacy">Website Privacy Policy</Link>.
      </p>
    </Section>

    <Section id="what" number={2} title="What cookies are">
      <p>
        Cookies are small text files that a website saves on your device. Similar technologies, such as your browser&apos;s local storage and
        tracking pixels, work in a similar way. In this policy, &quot;cookies&quot; covers all of them.
      </p>
    </Section>

    <Section id="consent" number={3} title="Your choices">
      <p>
        We only use strictly necessary cookies unless you choose <strong>Accept</strong> in our cookie banner. If you choose{' '}
        <strong>Reject</strong>, or ignore the banner, no analytics or marketing cookies are set.
      </p>
      <p>
        You can change your choice at any time using <CookieSettingsLink>Cookie settings</CookieSettingsLink>, which is also in the footer of
        every page. If you withdraw consent, we delete our analytics and marketing cookies from your browser and reload the page.
      </p>
      <p>
        You can also block or delete cookies in your browser settings. If you block strictly necessary cookies, parts of the Website may not work.
      </p>
    </Section>

    <Section id="necessary" number={4} title="Strictly necessary">
      <p>These are needed for the Website to work and don&apos;t need your consent.</p>
      <Table
        caption="Strictly necessary cookies and storage"
        headers={COOKIE_HEADERS}
        rows={NECESSARY_COOKIES}
      />
    </Section>

    <Section id="analytics" number={5} title="Analytics (with your consent)">
      <p>These help us understand how visitors use the Website, such as which pages are visited most. They are loaded through Google Tag Manager and Firebase.</p>
      <Table
        caption="Analytics cookies"
        headers={COOKIE_HEADERS}
        rows={ANALYTICS_COOKIES}
      />
    </Section>

    <Section id="marketing" number={6} title="Marketing (with your consent)">
      <p>These measure how well our advertising works and help show you relevant PRM Assist ads on other platforms.</p>
      <Table
        caption="Marketing cookies"
        headers={COOKIE_HEADERS}
        rows={MARKETING_COOKIES}
      />
      <p>
        These providers may also use this data for their own purposes. See the privacy policies
        of <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google</a>,{' '}
        <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">Meta</a> and{' '}
        <a href="https://www.linkedin.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">LinkedIn</a>.
      </p>
    </Section>

    <Section id="browser" number={7} title="Managing cookies in your browser">
      <List items={[
        <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a>,
        <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a>,
        <a href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" target="_blank" rel="noopener noreferrer">Firefox</a>,
        <a href="https://support.microsoft.com/en-us/microsoft-edge/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer">Microsoft Edge</a>,
      ]} />
    </Section>

    <Section id="changes" number={8} title="Changes to this policy">
      <p>We&apos;ll update this policy when the cookies we use change, and update the &quot;Last updated&quot; date.</p>
    </Section>

    <Section id="contact" number={9} title="Contact">
      <p><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
    </Section>

  </LegalPage>
);

export default CookiePolicy;
